import React, { useState, useMemo, useRef, useEffect } from 'react';
import { supabase, TABLES } from '../../supabase/supabase';

// ── Constants ─────────────────────────────────────────────────────────────────
const SVG_H = 480;
const M = { top: 80, right: 24, bottom: 30, left: 100 };
const PH = SVG_H - M.top  - M.bottom;
// Infinite-canvas horizontal sizing (in viewBox units):
const DEFAULT_H_STRETCH = 1.4; // viewBox plot-width per raw-x unit — higher = more breathing room
const STRETCH_MIN = 0.6;
const STRETCH_MAX = 3.5;
const MIN_PLOT_WIDTH = 700; // never narrower than this many viewBox units
const PERSONA_ROW_H = 92;   // px height of each persona swimlane row

const SESSION_COLORS = [
  '#3B82F6','#10B981','#F59E0B','#8B5CF6',
  '#EC4899','#06B6D4','#84CC16','#F97316',
  '#14B8A6','#6366F1','#EF4444','#A855F7',
];

// In annotation mode the chart is a fixed middle band (so quote/persona bands can
// scroll above/below it); otherwise it fills the whole viewport height.
const computeChartH = (totalH, annotationMode) => {
  const t = totalH || SVG_H;
  return annotationMode ? Math.max(240, Math.min(t - 220, t * 0.5)) : t;
};

// Ordinal time bands (must match JourneyMapCanvas TIME_BANDS labels)
const TIME_BAND_ORDER = ['seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'];
const bandRank = (b) => TIME_BAND_ORDER.indexOf(b);

// The representative duration between two tagged milestones in one session:
// the coarsest (largest) time band among the segments separating them.
const sessionBandBetween = (session, tagA, tagB) => {
  const ms = [...session.milestones].sort((a, b) => a.x_position - b.x_position);
  const ia = ms.findIndex(m => m.researcher_tag === tagA);
  const ib = ms.findIndex(m => m.researcher_tag === tagB);
  if (ia === -1 || ib === -1 || ia === ib) return null;
  const lo = Math.min(ia, ib), hi = Math.max(ia, ib);
  let best = null, bestRank = -1;
  for (let j = lo + 1; j <= hi; j++) {
    const r = bandRank(ms[j].time_from_previous);
    if (r > bestRank) { bestRank = r; best = ms[j].time_from_previous; }
  }
  return best; // null if no band data on the intervening segments
};

// ── Smooth curve helper (Catmull-Rom → cubic bezier) ──────────────────────────
const smoothPath = (pts) => {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  const parts = [`M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    parts.push(`C${cp1x.toFixed(1)},${cp1y.toFixed(1)},${cp2x.toFixed(1)},${cp2y.toFixed(1)},${p2.x.toFixed(1)},${p2.y.toFixed(1)}`);
  }
  return parts.join(' ');
};

// ── Piecewise time-warp ───────────────────────────────────────────────────────
// Given a session's anchor control points [{ raw, target }] (sorted by raw),
// remap any raw x_position onto the shared timeline. Each anchor pins a tagged
// milestone to its column; the segments BETWEEN anchors stretch/compress
// linearly so every tagged milestone lands on its column simultaneously —
// exactly like dragging multiple points of a bezier into alignment. Outside the
// outermost anchors we translate by the nearest anchor's offset.
const warpX = (rawX, controls) => {
  if (!controls || controls.length === 0) return rawX;
  if (controls.length === 1) return rawX + (controls[0].target - controls[0].raw);
  const first = controls[0];
  const last  = controls[controls.length - 1];
  if (rawX <= first.raw) return rawX + (first.target - first.raw);
  if (rawX >= last.raw)  return rawX + (last.target  - last.raw);
  for (let i = 0; i < controls.length - 1; i++) {
    const c0 = controls[i], c1 = controls[i + 1];
    if (rawX >= c0.raw && rawX <= c1.raw) {
      const span = c1.raw - c0.raw || 1;
      const t = (rawX - c0.raw) / span;
      return c0.target + t * (c1.target - c0.target);
    }
  }
  return rawX;
};

// ── Manual remap (SVG space) ──────────────────────────────────────────────────
// Second pass on top of auto-alignment: when a researcher drags a tag column,
// we remap SVG x-coordinates through control points {from, to}. Columns the
// researcher hasn't touched stay pinned (from === to), so only the dragged
// column and the curve segments around it move. Plot edges are also pinned.
const remapWithControls = (x, controls) => {
  if (!controls || controls.length < 2) return x;
  const first = controls[0], last = controls[controls.length - 1];
  if (x <= first.from) return x + (first.to - first.from);
  if (x >= last.from)  return x + (last.to - last.from);
  for (let i = 0; i < controls.length - 1; i++) {
    const c0 = controls[i], c1 = controls[i + 1];
    if (x >= c0.from && x <= c1.from) {
      const span = c1.from - c0.from || 1;
      const t = (x - c0.from) / span;
      return c0.to + t * (c1.to - c0.to);
    }
  }
  return x;
};

const interpY = (pts, x) => {
  if (pts.length === 0) return null;
  if (x < pts[0].x || x > pts[pts.length - 1].x) return null;
  const after  = pts.find(p => p.x >= x);
  const before = [...pts].reverse().find(p => p.x <= x);
  if (!after || !before) return null;
  if (after.x === before.x) return after.y;
  const t = (x - before.x) / (after.x - before.x);
  return before.y + t * (after.y - before.y);
};

// ── Quote attribution chip (read-only view) ──────────────────────────────────
// Shows participant colour + label always; reveals their segment tags on
// hover/click so stakeholders can see "who said this" without clutter.
const AttributionChip = ({ color, label, tags }) => {
  const [open, setOpen] = useState(false);
  const segs = (tags || []).map(t => t.name || `${t.tag_key}: ${t.tag_value}`);
  return (
    <span className="relative inline-flex">
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="flex items-center gap-1 text-[8px] font-black uppercase tracking-wide"
        title={segs.join(' · ')}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span style={{ color }}>{label}</span>
      </button>
      {open && segs.length > 0 && (
        <div className="absolute left-0 bottom-full mb-1 z-30 bg-rutabaga-dark text-white text-[8px] font-bold rounded px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none">
          {segs.join(' · ')}
        </div>
      )}
    </span>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const JourneyMapAnalysis = ({
  sessions, lanes, module: journeyModule, onSessionsUpdate,
  readOnly = false, initialViewMode,
  annotationMode = false, editable = false,
  personas = [], onPersonasChange,
  quotes = [], onQuotesChange,
}) => {
  const [viewMode, setViewMode]           = useState(initialViewMode || 'combined');
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [tagging, setTagging]             = useState(null);
  const [tagInput, setTagInput]           = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hovered, setHovered]             = useState(null);
  const tagInputRef = useRef(null);

  // ── Viewport measurement (drives infinite-canvas sizing) ───────────────────
  const scrollRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 1000, h: SVG_H });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewport({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sessions.length === 0]); // re-attach when the canvas mounts/unmounts

  // ── Manual tag dragging ────────────────────────────────────────────────────
  // manualTagX maps tag → fraction (0..1 across the plot). Stored as a fraction
  // so it survives canvas resizes (infinite canvas changes pixel width).
  const svgRef = useRef(null);
  const [manualTagX, setManualTagX] = useState(() => journeyModule?.config?.tag_positions || {});
  const [draggingTag, setDraggingTag] = useState(null);
  const [editingRole, setEditingRole] = useState(null); // persona id whose roster/colour popover is open
  const [stretch, setStretch] = useState(() => journeyModule?.config?.h_stretch ?? DEFAULT_H_STRETCH);
  const draggingTagRef = useRef(null);
  const manualTagXRef = useRef(manualTagX);
  const stretchRef = useRef(stretch);
  useEffect(() => { manualTagXRef.current = manualTagX; }, [manualTagX]);
  useEffect(() => { stretchRef.current = stretch; }, [stretch]);

  // Live view config — kept in a ref so successive partial saves (tag positions,
  // stretch) merge instead of clobbering each other (the module prop is a stale
  // snapshot that doesn't update after we write to the DB).
  const configRef = useRef(journeyModule?.config || {});
  useEffect(() => {
    configRef.current = journeyModule?.config || {};
    setManualTagX(journeyModule?.config?.tag_positions || {});
    setStretch(journeyModule?.config?.h_stretch ?? DEFAULT_H_STRETCH);
  }, [journeyModule?.id]);

  const persistConfig = async (patch) => {
    if (!journeyModule?.id) return;
    configRef.current = { ...configRef.current, ...patch };
    const { error } = await supabase.from(TABLES.MODULES)
      .update({ config: configRef.current }).eq('id', journeyModule.id);
    if (error) console.error('[analysis] saving view config failed:', error);
  };
  const saveConfig = (positions) => persistConfig({ tag_positions: positions });

  const clearOverride = (tag) => {
    setManualTagX(prev => {
      const next = { ...prev };
      delete next[tag];
      saveConfig(next);
      return next;
    });
  };
  // NOTE: the pointer-drag effect lives further down — it depends on svgW/pw,
  // which are declared in the layout memo below (avoids a TDZ on those consts).

  const allTags = useMemo(() => (
    [...new Set(sessions.flatMap(s => s.milestones.map(m => m.researcher_tag).filter(Boolean)))]
  ), [sessions]);

  const suggestions = useMemo(() => {
    const raw = tagInput.replace(/^[#/]/, '').toLowerCase().trim();
    if (!raw) return allTags;
    return allTags.filter(t => t.toLowerCase().includes(raw));
  }, [tagInput, allTags]);

  useEffect(() => {
    if (tagging) {
      setShowSuggestions(allTags.length > 0);
      tagInputRef.current?.focus();
    }
  }, [tagging, allTags.length]);

  // ── Auto-alignment computation ────────────────────────────────────────────
  //
  // For every tag that appears in 2+ different sessions, we shift EACH of those
  // sessions so their tagged milestone lands at the same X coordinate.
  // The target X for a tag = average raw x_position of all milestones with that tag.
  //
  // Each session uses its FIRST (chronologically earliest) tagged milestone as its
  // alignment anchor — we compute the offset once per session and shift all of
  // that session's milestones by the same amount, exactly like dragging a line
  // in Illustrator so one point snaps to the shared event column.
  // ─────────────────────────────────────────────────────────────────────────
  const { sessionPoints, trendPoints, columns, svgW, pw } = useMemo(() => {

    // 1. Build tag → [{ sessionId, x_position }] map
    const tagInstances = {};
    sessions.forEach(s => {
      // Sort milestones by x so we use the chronologically first one per session/tag
      const sorted = [...s.milestones].sort((a, b) => a.x_position - b.x_position);
      sorted.forEach(m => {
        if (!m.researcher_tag) return;
        const tag = m.researcher_tag;
        if (!tagInstances[tag]) tagInstances[tag] = [];
        // Only record the FIRST milestone per session per tag
        if (!tagInstances[tag].some(p => p.sessionId === s.id)) {
          tagInstances[tag].push({ sessionId: s.id, x: m.x_position });
        }
      });
    });

    // 2. Compute per-tag target X (the shared column position, in raw units).
    //    Average of each session's first-tagged milestone — "meet in the middle".
    //    Only tags appearing in 2+ sessions become alignment anchors.
    const tagTargets = {};
    Object.entries(tagInstances).forEach(([tag, pts]) => {
      if (pts.length >= 2) {
        tagTargets[tag] = pts.reduce((sum, p) => sum + p.x, 0) / pts.length;
      }
    });

    // 3. Build per-session warp control points.
    //    One control per shared tag: { raw: where the milestone is now,
    //    target: where its column lives }. Sorted by raw, monotonic targets.
    const sessionControls = {};
    sessions.forEach(s => {
      const sorted = [...s.milestones].sort((a, b) => a.x_position - b.x_position);
      const seen = new Set();
      const ctrls = [];
      sorted.forEach(m => {
        if (m.researcher_tag && tagTargets[m.researcher_tag] !== undefined && !seen.has(m.researcher_tag)) {
          seen.add(m.researcher_tag);
          ctrls.push({ raw: m.x_position, target: tagTargets[m.researcher_tag] });
        }
      });
      ctrls.sort((a, b) => a.raw - b.raw);
      // NOTE: no monotonic clamp. Each control pins its tagged milestone to its
      // exact column target — even when two sessions tagged events in different
      // orders (the segment between such anchors may slope backward, but every
      // tagged dot still lands precisely on its column, which is the goal).
      sessionControls[s.id] = ctrls;
    });

    // 4. Effective X range (after warping)
    const allX = sessions.flatMap(s =>
      s.milestones.map(m => warpX(m.x_position, sessionControls[s.id]))
    );
    if (allX.length === 0) return { sessionPoints: [], trendPoints: [], columns: [], svgW: SVG_H * 2, pw: MIN_PLOT_WIDTH };

    const minX   = Math.min(...allX);
    const maxX   = Math.max(...allX);
    const xRange = maxX - minX || 1;

    // ── Infinite-canvas horizontal sizing ──────────────────────────────────
    // Plot width grows with the data span (so milestones never crowd), but is
    // floored so it always fills at least the visible viewport. The SVG is
    // scaled uniformly to fill the container height (factor k), so the minimum
    // viewBox width needed to cover the viewport width is viewport.w / k.
    const k = computeChartH(viewport.h, annotationMode) / SVG_H;
    const minPwForViewport = (viewport.w || 0) / k - M.left - M.right;
    const pw = Math.max(MIN_PLOT_WIDTH, minPwForViewport, xRange * stretch);
    const svgW = M.left + pw + M.right;

    const toSvgX = (rawX, sId) => M.left + ((warpX(rawX, sessionControls[sId]) - minX) / xRange) * pw;
    const toSvgY = (yPos) => M.top + (1 - yPos) * PH;

    // 5. Per-session SVG points
    const sessionPoints = sessions.map((session, idx) => ({
      sessionId: session.id,
      color: SESSION_COLORS[idx % SESSION_COLORS.length],
      isShifted: (sessionControls[session.id] || []).length > 0,
      points: session.milestones
        .map(m => ({
          id: m.id,
          x: toSvgX(m.x_position, session.id),
          y: toSvgY(m.y_position),
          label: m.label,
          tag: m.researcher_tag,
        }))
        .sort((a, b) => a.x - b.x),
    }));

    // 6. Tag columns: position = average SVG-X of all milestones carrying that tag
    //    (after alignment these should all be very close together)
    const cols = Object.entries(tagInstances).map(([tag, _]) => {
      const pts = sessionPoints.flatMap(sp => sp.points.filter(p => p.tag === tag));
      if (pts.length === 0) return null;
      const avgX = pts.reduce((sum, p) => sum + p.x, 0) / pts.length;
      const avgY = pts.reduce((sum, p) => sum + p.y, 0) / pts.length;
      const isAligned = Boolean(tagTargets[tag]); // true if 2+ sessions share this tag
      return { tag, avgX, avgY, count: pts.length, isAligned };
    }).filter(Boolean).sort((a, b) => a.avgX - b.avgX);

    // 7. Trend line (sample 60 points, require ≥20% of sessions at each X)
    const trendPts = [];
    const threshold = Math.max(1, Math.floor(sessionPoints.length * 0.2));
    for (let i = 0; i <= 60; i++) {
      const svgX = M.left + (i / 60) * pw;
      const ys = sessionPoints.map(sp => interpY(sp.points, svgX)).filter(y => y !== null);
      if (ys.length >= threshold) {
        trendPts.push({ x: svgX, y: ys.reduce((a, b) => a + b, 0) / ys.length });
      }
    }

    return { sessionPoints, trendPoints: trendPts, columns: cols, svgW, pw };
  }, [sessions, viewport.w, viewport.h, stretch, annotationMode]);

  // Pointer-drag helpers live in the render body below (need svgW/pw from the
  // memo) and are attached directly to each handle via pointer capture.

  // ── Tag save ──────────────────────────────────────────────────────────────
  const saveTag = async (tag) => {
    const rawTag = (tag ?? tagInput).replace(/^[#/]/, '').trim();
    if (!rawTag || !tagging) { setTagging(null); setTagInput(''); setShowSuggestions(false); return; }

    await supabase
      .from(TABLES.JOURNEY_MAP_PLACEMENTS)
      .update({ researcher_tag: rawTag })
      .eq('id', tagging.milestoneId);

    onSessionsUpdate?.(prev => prev.map(s => ({
      ...s,
      milestones: s.milestones.map(m =>
        m.id === tagging.milestoneId ? { ...m, researcher_tag: rawTag } : m
      ),
    })));

    setTagging(null);
    setTagInput('');
    setShowSuggestions(false);
  };

  // ── Lane labels ──────────────────────────────────────────────────────────
  const laneLabels = useMemo(() => {
    const top    = lanes.find(l => Math.abs(l.y_position - 1.0) < 0.1);
    const middle = lanes.find(l => Math.abs(l.y_position - 0.5) < 0.1);
    const bottom = lanes.find(l => Math.abs(l.y_position - 0.0) < 0.1);
    return [
      { label: top?.label    || 'Hero',               svgY: M.top },
      { label: middle?.label || 'Meets Expectations', svgY: M.top + PH * 0.5 },
      { label: bottom?.label || 'Irreconcilable',     svgY: M.top + PH },
    ];
  }, [lanes]);

  const displayedPoints = viewMode === 'individual' && selectedSessionId
    ? sessionPoints.filter(sp => sp.sessionId === selectedSessionId)
    : sessionPoints;

  const alignedTagCount = columns.filter(c => c.isAligned).length;

  // Uniform scale: SVG fills the chart band height (full viewport, or the fixed
  // middle band in annotation mode); width follows from the viewBox width.
  const k = computeChartH(viewport.h, annotationMode) / SVG_H;

  // ── Persona swimlane editing ───────────────────────────────────────────────
  const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const addPersona = () => onPersonasChange?.([
    ...personas,
    { id: newId(), label: 'New role', color: SESSION_COLORS[personas.length % SESSION_COLORS.length], order: personas.length, cells: {}, sessionIds: [] },
  ]);
  const updatePersona = (id, patch) => onPersonasChange?.(personas.map(p => (p.id === id ? { ...p, ...patch } : p)));
  const updateCell = (id, tag, text) => onPersonasChange?.(personas.map(p => (
    p.id === id ? { ...p, cells: { ...(p.cells || {}), [tag]: text } } : p
  )));
  const removePersona = (id) => onPersonasChange?.(personas.filter(p => p.id !== id));

  // ── Role rosters ────────────────────────────────────────────────────────────
  // Each participant belongs to at most one role. roleBySession maps a session to
  // its role; assigning to a new role removes it from any other.
  const UNASSIGNED_COLOR = '#9CA3AF';
  const roleBySession = {};
  personas.forEach(p => (p.sessionIds || []).forEach(sid => { if (!(sid in roleBySession)) roleBySession[sid] = p; }));
  const assignParticipant = (roleId, sessionId) => onPersonasChange?.(personas.map(p => {
    const has = (p.sessionIds || []).includes(sessionId);
    if (p.id === roleId) return has ? p : { ...p, sessionIds: [...(p.sessionIds || []), sessionId] };
    return has ? { ...p, sessionIds: p.sessionIds.filter(s => s !== sessionId) } : p; // one role per participant
  }));
  const unassignParticipant = (roleId, sessionId) => onPersonasChange?.(personas.map(p => (
    p.id === roleId ? { ...p, sessionIds: (p.sessionIds || []).filter(s => s !== sessionId) } : p
  )));
  // Effective journey-line colour: in annotation mode it's the role colour (gray if unassigned).
  const sessionColor = (sessionId, fallback) => {
    if (!annotationMode) return fallback;
    const role = roleBySession[sessionId];
    return role ? role.color : UNASSIGNED_COLOR;
  };
  // Use the session's stable project participant number (not its array index, which
  // shifts under filtering / in snapshots). Falls back to index for older data.
  const pNumber = (idx) => sessions[idx]?.participantNumber ?? (idx + 1);
  const pLabel = (idx) => `P${String(pNumber(idx)).padStart(2, '0')}`;
  const pLabelBySession = (sid) => { const i = sessions.findIndex(s => s.id === sid); return i >= 0 ? pLabel(i) : '—'; };

  // ── Quote editing + participant attribution ────────────────────────────────
  const addQuote = (tag) => onQuotesChange?.([
    ...quotes,
    { id: newId(), tag, text: '', sessionId: null, order: quotes.filter(q => q.tag === tag).length },
  ]);
  const updateQuote = (id, patch) => onQuotesChange?.(quotes.map(q => (q.id === id ? { ...q, ...patch } : q)));
  const removeQuote = (id) => onQuotesChange?.(quotes.filter(q => q.id !== id));
  // A quote keeps its real speaker (sessionId) but DISPLAYS as their role + role colour.
  const sessionMeta = (sessionId) => {
    const idx = sessions.findIndex(s => s.id === sessionId);
    if (idx < 0) return null;
    const role = roleBySession[sessionId] || null;
    return {
      idx,
      pLabel: pLabel(idx),
      role,
      roleLabel: role ? role.label : 'Unassigned',
      color: role ? role.color : UNASSIGNED_COLOR,
      tags: sessions[idx].tags || [],
    };
  };

  // ── Pointer-drag handlers for tag handles (need svgW/pw, in scope here) ────
  const fracFromClientX = (clientX) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    const vbX = ((clientX - r.left) / r.width) * svgW;
    return Math.max(0, Math.min(1, (vbX - M.left) / pw));
  };
  const onHandleDown = (tag, e) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    draggingTagRef.current = tag;
    setDraggingTag(tag);
  };
  const onHandleMove = (tag, e) => {
    if (draggingTagRef.current !== tag) return; // ignore plain hover moves
    let frac = fracFromClientX(e.clientX);
    if (frac == null) return;
    // Neighbour-clamp: a column can't be dragged past its siblings (which would
    // fold the curve). columns is sorted by auto position, giving stable order.
    const i = columns.findIndex(c => c.tag === tag);
    const effFrac = (c) => ((manualTagX[c.tag] != null ? M.left + manualTagX[c.tag] * pw : c.avgX) - M.left) / pw;
    const pad = 0.012;
    const lower = i > 0                  ? effFrac(columns[i - 1]) + pad : 0;
    const upper = i < columns.length - 1 ? effFrac(columns[i + 1]) - pad : 1;
    frac = Math.max(lower, Math.min(upper, frac));
    setManualTagX(prev => ({ ...prev, [tag]: frac }));
  };
  const onHandleUp = (tag, e) => {
    if (draggingTagRef.current !== tag) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    draggingTagRef.current = null;
    setDraggingTag(null);
    saveConfig(manualTagXRef.current);
  };

  // ── Apply manual drag overrides as an SVG-space remap ──────────────────────
  const rx = useMemo(() => {
    const hasOverride = columns.some(c => manualTagX[c.tag] != null);
    if (!hasOverride) return (x) => x;
    const controls = columns.map(c => ({
      from: c.avgX,
      to: manualTagX[c.tag] != null ? M.left + manualTagX[c.tag] * pw : c.avgX,
    }));
    controls.push({ from: M.left, to: M.left });
    controls.push({ from: M.left + pw, to: M.left + pw });
    controls.sort((a, b) => a.from - b.from);
    const dedup = [];
    for (const c of controls) {
      if (!dedup.length || Math.abs(dedup[dedup.length - 1].from - c.from) > 0.01) dedup.push(c);
    }
    return (x) => remapWithControls(x, dedup);
  }, [columns, manualTagX, pw]);

  const renderColumns = columns.map(c => ({ ...c, x: rx(c.avgX) }));
  const renderTrend   = trendPoints.map(p => ({ ...p, x: rx(p.x) }));
  const renderPoints  = displayedPoints.map(sp => ({
    ...sp,
    color: sessionColor(sp.sessionId, sp.color), // role colour (or gray) in annotation mode
    points: sp.points.map(p => ({ ...p, x: rx(p.x) })).sort((a, b) => a.x - b.x),
  }));

  // ── Time between adjacent tagged events (for the trend line) ───────────────
  // For each pair of neighbouring columns, gather the per-session durations and
  // show one band if everyone agrees, else "shortest to longest".
  const timeLabels = useMemo(() => {
    const out = [];
    for (let i = 0; i < columns.length - 1; i++) {
      const A = columns[i], B = columns[i + 1];
      const ranks = sessions
        .map(s => sessionBandBetween(s, A.tag, B.tag))
        .map(bandRank)
        .filter(r => r >= 0);
      if (ranks.length === 0) continue;
      const minR = Math.min(...ranks), maxR = Math.max(...ranks);
      out.push({
        tagA: A.tag,
        tagB: B.tag,
        label: minR === maxR ? TIME_BAND_ORDER[minR] : `${TIME_BAND_ORDER[minR]} to ${TIME_BAND_ORDER[maxR]}`,
      });
    }
    return out;
  }, [columns, sessions]);

  return (
    <div className="flex flex-col h-full">

      {/* ── Controls bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-100 shrink-0 flex-wrap">

        <div className="flex rounded-md overflow-hidden border border-gray-200">
          {['combined', 'individual'].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors
                ${viewMode === mode ? 'bg-rutabaga-dark text-white' : 'bg-white text-gray-400 hover:bg-gray-50'}`}>
              {mode}
            </button>
          ))}
        </div>

        {viewMode === 'individual' && (
          <select value={selectedSessionId || ''} onChange={e => setSelectedSessionId(e.target.value)}
            className="text-[10px] font-bold border border-gray-200 rounded px-2 py-1.5 text-gray-600 outline-none focus:border-rutabaga-pink">
            <option value="">Select session…</option>
            {sessionPoints.map((sp) => (
              <option key={sp.sessionId} value={sp.sessionId}>
                {pLabelBySession(sp.sessionId)} — {sp.points.length} milestone{sp.points.length !== 1 ? 's' : ''}
              </option>
            ))}
          </select>
        )}

        {/* Horizontal spacing (stretch) control */}
        {!readOnly && (
        <div className="flex items-center gap-1.5" title="Horizontal spacing of the timeline">
          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Spacing</span>
          <input
            type="range"
            min={STRETCH_MIN} max={STRETCH_MAX} step={0.1}
            value={stretch}
            onChange={(e) => setStretch(parseFloat(e.target.value))}
            onPointerUp={() => persistConfig({ h_stretch: stretchRef.current })}
            onKeyUp={() => persistConfig({ h_stretch: stretchRef.current })}
            className="w-24 accent-rutabaga-pink cursor-pointer"
          />
          {Math.abs(stretch - DEFAULT_H_STRETCH) > 0.001 && (
            <button
              onClick={() => { setStretch(DEFAULT_H_STRETCH); persistConfig({ h_stretch: DEFAULT_H_STRETCH }); }}
              className="text-[9px] font-black text-gray-300 hover:text-rutabaga-pink uppercase tracking-widest"
              title="Reset spacing">
              ↺
            </button>
          )}
        </div>
        )}

        {/* Auto-alignment status */}
        {alignedTagCount > 0 ? (
          <span className="text-[9px] font-black text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
            ⟺ Aligned on {alignedTagCount} shared tag{alignedTagCount !== 1 ? 's' : ''}
          </span>
        ) : allTags.length > 0 ? (
          <span className="text-[9px] font-black text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
            Tag more sessions with the same name to align lines
          </span>
        ) : null}

        {/* Color legend — by role in annotation mode, else by participant */}
        {annotationMode ? (
          <div className="ml-auto flex items-center gap-2.5 flex-wrap justify-end">
            {personas.filter(p => (p.sessionIds || []).length > 0).slice(0, 8).map(p => (
              <div key={p.id} className="flex items-center gap-1">
                <div className="w-5 h-0.5 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="text-[9px] font-bold" style={{ color: p.color }}>{p.label}</span>
              </div>
            ))}
            {displayedPoints.some(sp => !roleBySession[sp.sessionId]) && (
              <div className="flex items-center gap-1">
                <div className="w-5 h-0.5 rounded-full" style={{ backgroundColor: UNASSIGNED_COLOR }} />
                <span className="text-[9px] font-bold text-gray-400">Unassigned</span>
              </div>
            )}
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-2.5">
            {displayedPoints.slice(0, 8).map((sp) => (
              <div key={sp.sessionId} className="flex items-center gap-1">
                <div className="w-5 h-0.5 rounded-full" style={{ backgroundColor: sp.color }} />
                <span className="text-[9px] text-gray-400 font-bold">{pLabelBySession(sp.sessionId)}</span>
              </div>
            ))}
            {displayedPoints.length > 8 && (
              <span className="text-[9px] text-gray-400 font-bold">+{displayedPoints.length - 8}</span>
            )}
          </div>
        )}
      </div>

      {/* ── Canvas ────────────────────────────────────────────────────────── */}
      {sessionPoints.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-black text-rutabaga-dark">No journey map data yet</p>
            <p className="text-gray-400 text-sm mt-1">Complete sessions with the journey map module to see results here.</p>
          </div>
        </div>
      ) : (
        <div ref={scrollRef} className={`flex-1 bg-white relative ${annotationMode ? 'overflow-x-auto overflow-y-hidden' : 'overflow-auto'}`}>
          {!readOnly && (
            <p className="absolute top-2 right-4 z-10 text-[9px] text-gray-300 font-bold pointer-events-none">
              Click a milestone dot to tag it · type / to see existing tags
            </p>
          )}

          <div style={{ width: svgW * k }} className={annotationMode ? 'h-full flex flex-col min-h-0' : 'contents'}>

            {/* ── Quotes band ──────────────────────────────────────────────── */}
            {annotationMode && (
              <div className="flex-1 min-h-0 border-b border-gray-200 bg-white relative" style={{ width: svgW * k }}>
                <div className="sticky left-0 top-0 z-20 bg-white px-3 py-1.5 text-[9px] font-black text-gray-300 uppercase tracking-widest" style={{ width: M.left * k }}>
                  Quotes
                </div>
                {renderColumns.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-300 font-bold pointer-events-none">
                    Tag milestones to anchor quotes
                  </div>
                )}
                {renderColumns.map(col => {
                  const colQuotes = quotes.filter(q => q.tag === col.tag).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                  if (!editable && colQuotes.length === 0) return null;
                  const dim = hovered?.tag && hovered.tag !== col.tag;
                  return (
                    <div key={col.tag}
                      className="absolute flex flex-col gap-1.5 overflow-y-auto px-1 pt-8 pb-2"
                      style={{ left: col.x * k, top: 0, bottom: 0, width: 168, transform: 'translateX(-50%)', opacity: dim ? 0.3 : 1, transition: 'opacity 0.15s' }}>
                      {colQuotes.map(q => {
                        const meta = sessionMeta(q.sessionId);
                        const color = meta?.color || '#9CA3AF';
                        return (
                          <div key={q.id} className="rounded-md border border-gray-200 bg-white shadow-sm shrink-0"
                            style={{ borderLeftWidth: 3, borderLeftColor: color }}>
                            <div className="p-2">
                              {editable ? (
                                <textarea
                                  value={q.text}
                                  onChange={e => updateQuote(q.id, { text: e.target.value })}
                                  placeholder="“quote…”"
                                  rows={3}
                                  className="w-full text-[10px] italic leading-snug text-gray-700 bg-transparent outline-none resize-none"
                                />
                              ) : (
                                <p className="text-[10px] italic leading-snug text-gray-700">“{q.text}”</p>
                              )}
                              <div className="mt-1.5 flex items-center justify-between gap-1">
                                {editable ? (
                                  <select
                                    value={q.sessionId || ''}
                                    onChange={e => updateQuote(q.id, { sessionId: e.target.value || null })}
                                    className="text-[8px] font-bold text-gray-500 bg-transparent outline-none max-w-[150px]"
                                  >
                                    <option value="">— attribute —</option>
                                    {sessions.map((s, i) => (
                                      <option key={s.id} value={s.id}>
                                        {pLabel(i)} · {roleBySession[s.id]?.label || 'Unassigned'}
                                      </option>
                                    ))}
                                  </select>
                                ) : meta ? (
                                  <AttributionChip color={color} label={`${meta.pLabel} · ${meta.roleLabel}`} tags={meta.tags} />
                                ) : <span />}
                                {editable && (
                                  <button onClick={() => removeQuote(q.id)}
                                    className="text-[10px] leading-none text-gray-300 hover:text-rutabaga-pink shrink-0">✕</button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {editable && (
                        <button onClick={() => addQuote(col.tag)}
                          className="text-[9px] font-black text-rutabaga-pink/70 hover:text-rutabaga-pink uppercase tracking-widest py-1 shrink-0">
                          + Quote
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Chart band (fixed middle in annotation mode) ──────────────── */}
            <div className={`relative ${annotationMode ? 'shrink-0' : ''}`} style={{ width: svgW * k, height: SVG_H * k }}>

              {/* Sticky Y-axis lane labels (pinned while scrolling horizontally) */}
              <div className="pointer-events-none" style={{ position: 'sticky', left: 0, top: 0, width: 0, height: 0, zIndex: 5 }}>
                <div style={{ position: 'absolute', left: 0, top: 0, width: (M.left - 6) * k, height: SVG_H * k, background: 'white' }}>
                  {laneLabels.map(({ label, svgY }) => (
                    <div key={label}
                      className="text-[10px] font-bold text-gray-400 text-right leading-tight"
                      style={{ position: 'absolute', right: 8, width: (M.left - 16) * k, top: (svgY / SVG_H) * SVG_H * k, transform: 'translateY(-50%)' }}>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

            <svg ref={svgRef} viewBox={`0 0 ${svgW} ${SVG_H}`} width="100%" height="100%"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              style={{ display: 'block', userSelect: 'none', WebkitUserSelect: 'none', touchAction: draggingTag ? 'none' : 'auto', cursor: draggingTag ? 'ew-resize' : 'default' }}>

              {/* Lane dividers */}
              {[M.top + PH / 3, M.top + PH * 2 / 3].map((y, i) => (
                <line key={i} x1={M.left} y1={y} x2={svgW - M.right} y2={y}
                  stroke="#F3F4F6" strokeWidth={1} />
              ))}

              {/* Tag columns */}
              {renderColumns.map(col => {
                const isDrag = draggingTag === col.tag;
                const overridden = manualTagX[col.tag] != null;
                const stroke = col.isAligned ? '#E02F50' : '#9CA3AF';
                return (
                <g key={col.tag}>
                  {/* Full-height vertical line */}
                  <line x1={col.x} y1={M.top} x2={col.x} y2={M.top + PH}
                    stroke={col.isAligned ? '#E02F50' : '#D1D5DB'}
                    strokeWidth={col.isAligned ? 1.5 : 1}
                    strokeDasharray={col.isAligned ? 'none' : '4,3'}
                    strokeOpacity={isDrag ? 1 : 0.7}
                  />
                  {/* Tick above */}
                  <line x1={col.x} y1={M.top - 10} x2={col.x} y2={M.top}
                    stroke={stroke} strokeWidth={col.isAligned ? 2 : 1} />
                  {/* Tag name */}
                  <text x={col.x} y={M.top - 13} textAnchor="middle"
                    fontSize={9} fontFamily="Roboto,sans-serif" fontWeight="700"
                    fill={col.isAligned ? '#E02F50' : '#6B7280'} letterSpacing="0.04em"
                    style={{ pointerEvents: 'none' }}>
                    #{col.tag}
                  </text>
                  {/* Session count */}
                  <text x={col.x} y={M.top - 25} textAnchor="middle"
                    fontSize={8} fontFamily="Roboto,sans-serif"
                    fill={col.isAligned ? '#E02F50' : '#9CA3AF'}
                    style={{ pointerEvents: 'none' }}>
                    {col.count} session{col.count !== 1 ? 's' : ''}{col.isAligned ? ' ⟺' : ''}
                  </text>
                  {/* Drag handle + hit area (editing only) */}
                  {!readOnly && (
                    <>
                      <rect x={col.x - 9} y={M.top - 42} width={18} height={7} rx={2}
                        fill={overridden ? '#E02F50' : '#D1D5DB'} opacity={isDrag ? 1 : 0.85}
                        style={{ pointerEvents: 'none' }} />
                      <rect x={col.x - 12} y={M.top - 44} width={24} height={44}
                        fill="transparent" style={{ cursor: 'ew-resize', pointerEvents: 'all' }}
                        onPointerDown={(e) => onHandleDown(col.tag, e)}
                        onPointerMove={(e) => onHandleMove(col.tag, e)}
                        onPointerUp={(e) => onHandleUp(col.tag, e)}
                        onPointerCancel={(e) => onHandleUp(col.tag, e)}
                        onDoubleClick={() => clearOverride(col.tag)}>
                        <title>{overridden ? 'Drag to move · double-click to reset' : 'Drag to move this tag'}</title>
                      </rect>
                    </>
                  )}
                </g>
                );
              })}

              {/* Individual session lines */}
              {renderPoints.map(sp => (
                sp.points.length >= 2 && (
                  <path key={sp.sessionId}
                    d={smoothPath(sp.points)}
                    fill="none"
                    stroke={sp.color}
                    strokeWidth={viewMode === 'individual' ? 2 : 1.5}
                    strokeDasharray={viewMode === 'individual' ? 'none' : '5,4'}
                    strokeOpacity={viewMode === 'individual' ? 0.9 : 0.55}
                    strokeLinecap="round"
                  />
                )
              ))}

              {/* Trend line (combined view) */}
              {viewMode === 'combined' && renderTrend.length >= 2 && (
                <>
                  <path d={smoothPath(renderTrend)} fill="none"
                    stroke="#1F2937" strokeWidth={5}
                    strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
                  {renderColumns.map(col => {
                    const tY = interpY(renderTrend, col.x);
                    return tY !== null
                      ? <circle key={col.tag} cx={col.x} cy={tY}
                          r={6} fill="#1F2937" stroke="white" strokeWidth={2} />
                      : null;
                  })}
                </>
              )}

              {/* Time-between-events labels (combined view) */}
              {viewMode === 'combined' && timeLabels.map(({ tagA, tagB, label }) => {
                const a = renderColumns.find(c => c.tag === tagA);
                const b = renderColumns.find(c => c.tag === tagB);
                if (!a || !b) return null;
                const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
                const midX = (x1 + x2) / 2;
                const y = M.top + 14;
                if (x2 - x1 < 24) return null; // too tight to label
                return (
                  <g key={`${tagA}~${tagB}`} style={{ pointerEvents: 'none' }}>
                    <line x1={x1 + 3} y1={y} x2={x2 - 3} y2={y} stroke="#D1D5DB" strokeWidth={1} />
                    <line x1={x1 + 3} y1={y - 3} x2={x1 + 3} y2={y + 3} stroke="#D1D5DB" strokeWidth={1} />
                    <line x1={x2 - 3} y1={y - 3} x2={x2 - 3} y2={y + 3} stroke="#D1D5DB" strokeWidth={1} />
                    <rect x={midX - label.length * 2.6 - 5} y={y - 6} width={label.length * 5.2 + 10} height={12} rx={6} fill="white" />
                    <text x={midX} y={y + 3} textAnchor="middle"
                      fontSize={8} fontFamily="Roboto,sans-serif" fontWeight="700"
                      fill="#6B7280" letterSpacing="0.02em">
                      {label}
                    </text>
                  </g>
                );
              })}

              {/* Milestone dots */}
              {renderPoints.map(sp =>
                sp.points.map(pt => {
                  const isHov = hovered?.id === pt.id;
                  return (
                    <g key={pt.id}>
                      {pt.tag && (
                        <circle cx={pt.x} cy={pt.y} r={10}
                          fill={sp.color} fillOpacity={0.12} stroke="none" />
                      )}
                      <circle
                        cx={pt.x} cy={pt.y}
                        r={isHov ? 7 : 5}
                        fill={pt.tag ? sp.color : 'white'}
                        stroke={sp.color}
                        strokeWidth={pt.tag ? 0 : 2}
                        style={{ cursor: readOnly ? 'default' : 'pointer', transition: 'r 0.1s' }}
                        onMouseEnter={() => setHovered({ id: pt.id, label: pt.label, tag: pt.tag, svgX: pt.x, svgY: pt.y })}
                        onMouseLeave={() => setHovered(null)}
                        onClick={readOnly ? undefined : () => {
                          setTagging({ milestoneId: pt.id, svgX: pt.x, svgY: pt.y, currentTag: pt.tag });
                          setTagInput(pt.tag ? `#${pt.tag}` : '');
                          setShowSuggestions(true);
                        }}
                      />
                      {viewMode === 'individual' && pt.label && (
                        <g>
                          <line x1={pt.x} y1={pt.y - 7} x2={pt.x} y2={M.top - 16}
                            stroke={sp.color} strokeWidth={0.5} strokeOpacity={0.25} />
                          <text x={pt.x} y={M.top - 18} textAnchor="middle"
                            fontSize={8} fontFamily="Roboto,sans-serif" fontStyle="italic"
                            fill="#6B7280" style={{ pointerEvents: 'none' }}>
                            {pt.label.length > 28 ? pt.label.slice(0, 28) + '…' : pt.label}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })
              )}
            </svg>

            {/* Hover tooltip */}
            {hovered && (
              <div className="absolute pointer-events-none z-10 bg-rutabaga-dark text-white text-[10px] font-bold px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap"
                style={{
                  left: `${(hovered.svgX / svgW) * 100}%`,
                  top:  `${(hovered.svgY / SVG_H) * 100}%`,
                  transform: 'translate(-50%, -130%)',
                }}>
                {hovered.label || '(no label)'}
                {hovered.tag && <span className="ml-1.5 text-rutabaga-pink">#{hovered.tag}</span>}
              </div>
            )}

            {/* Tag assignment popover */}
            {tagging && (
              <div className="absolute z-20 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
                style={{
                  left: `${(tagging.svgX / svgW) * 100}%`,
                  top:  `${(tagging.svgY / SVG_H) * 100}%`,
                  transform: 'translate(-50%, -115%)',
                  minWidth: 210,
                }}>
                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                  Common tag
                  {tagging.currentTag && (
                    <span className="ml-2 text-rutabaga-pink normal-case font-bold">#{tagging.currentTag}</span>
                  )}
                </p>
                <input
                  ref={tagInputRef}
                  value={tagInput}
                  onChange={e => { setTagInput(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') saveTag();
                    if (e.key === 'Escape') { setTagging(null); setTagInput(''); setShowSuggestions(false); }
                  }}
                  placeholder="type or / to browse…"
                  className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 outline-none focus:border-rutabaga-pink font-mono"
                />

                {showSuggestions && suggestions.length > 0 && (
                  <div className="mt-1.5 border border-gray-100 rounded overflow-hidden max-h-32 overflow-y-auto">
                    {suggestions.map(tag => (
                      <button key={tag}
                        onMouseDown={e => { e.preventDefault(); saveTag(tag); }}
                        className="w-full text-left px-2.5 py-1.5 text-[10px] font-mono text-gray-600 hover:bg-rutabaga-pink/5 hover:text-rutabaga-pink transition-colors">
                        #{tag}
                      </button>
                    ))}
                  </div>
                )}

                {allTags.length === 0 && (
                  <p className="text-[9px] text-gray-300 mt-1.5">No tags yet — type a name and press Enter.</p>
                )}

                <div className="flex gap-2 mt-2">
                  <button onClick={() => saveTag()}
                    className="flex-1 bg-rutabaga-pink text-white text-[9px] font-black uppercase tracking-widest py-1 rounded hover:bg-rutabaga-pink-hover transition-colors">
                    Save
                  </button>
                  <button onClick={() => { setTagging(null); setTagInput(''); setShowSuggestions(false); }}
                    className="px-2 text-[9px] font-black text-gray-400 hover:text-gray-600">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            </div>

            {/* ── Persona / role swimlanes ──────────────────────────────── */}
            {annotationMode && (
              <div className="flex-1 min-h-0 overflow-y-auto border-t border-gray-200 bg-gray-50/40 relative" style={{ width: svgW * k }}>
                <div className="sticky left-0 top-0 z-20 bg-gray-50 px-3 py-1.5 text-[9px] font-black text-gray-300 uppercase tracking-widest border-b border-gray-100" style={{ width: M.left * k }}>
                  Roles
                </div>

                {personas.length === 0 && (
                  <div className="px-3 py-4 text-[10px] text-gray-300 font-bold sticky left-0" style={{ width: 320 }}>
                    {editable ? 'Add a role to describe what each persona is doing at each tagged moment.' : 'No roles added.'}
                  </div>
                )}

                {[...personas].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(persona => (
                  <div key={persona.id} className="relative border-b border-gray-100" style={{ height: PERSONA_ROW_H }}>
                    {/* sticky label gutter */}
                    <div className="sticky left-0 z-10 bg-gray-50 border-r border-gray-100 flex flex-col justify-center px-2"
                      style={{ width: M.left * k, height: PERSONA_ROW_H }}>
                      <div className="flex items-center gap-1">
                        {editable ? (
                          <button onClick={() => setEditingRole(editingRole === persona.id ? null : persona.id)}
                            className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: persona.color }}
                            title="Edit role colour & participants" />
                        ) : (
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: persona.color }} />
                        )}
                        {editable ? (
                          <input
                            value={persona.label}
                            onChange={(e) => updatePersona(persona.id, { label: e.target.value })}
                            className="w-full text-[10px] font-black bg-transparent outline-none min-w-0"
                            style={{ color: persona.color }}
                          />
                        ) : (
                          <span className="text-[10px] font-black truncate" style={{ color: persona.color }}>{persona.label}</span>
                        )}
                      </div>
                      <div className="text-[8px] font-bold text-gray-400 mt-0.5 truncate">
                        {(persona.sessionIds || []).map(sid => sessions.findIndex(s => s.id === sid)).filter(i => i >= 0).map(i => pLabel(i)).join(' ') || (editable ? 'no participants' : '')}
                      </div>
                      {editable && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <button onClick={() => setEditingRole(editingRole === persona.id ? null : persona.id)}
                            className="text-[8px] font-black text-rutabaga-pink hover:text-rutabaga-pink-hover uppercase tracking-widest">
                            Assign
                          </button>
                          <button onClick={() => removePersona(persona.id)}
                            className="text-[8px] font-bold text-gray-300 hover:text-rutabaga-pink uppercase tracking-widest">
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    {/* per-tag task cells */}
                    {renderColumns.map(col => {
                      const val = persona.cells?.[col.tag] || '';
                      if (!editable && !val.trim()) return null;
                      return (
                        <div key={col.tag} className="absolute" style={{ left: col.x * k, top: 8, transform: 'translateX(-50%)', width: 132 }}>
                          {editable ? (
                            <textarea
                              value={val}
                              onChange={(e) => updateCell(persona.id, col.tag, e.target.value)}
                              placeholder="what is this role doing?"
                              className="w-full text-[10px] leading-snug text-gray-600 bg-white border border-gray-200 rounded p-1.5 outline-none focus:border-rutabaga-pink resize-none"
                              style={{ height: PERSONA_ROW_H - 16 }}
                            />
                          ) : (
                            <div className="w-full text-[10px] leading-snug text-gray-600 bg-white border border-gray-100 rounded p-1.5 overflow-y-auto"
                              style={{ height: PERSONA_ROW_H - 16 }}>
                              {val}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Role colour + participant roster popover */}
                    {editable && editingRole === persona.id && (
                      <div className="absolute z-30 bg-white border border-gray-200 rounded-lg shadow-xl p-2.5"
                        style={{ left: M.left * k + 6, top: 6, width: 240 }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Role colour & participants</span>
                          <button onClick={() => setEditingRole(null)} className="text-[10px] text-gray-300 hover:text-gray-600">✕</button>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {SESSION_COLORS.map(c => (
                            <button key={c} onClick={() => updatePersona(persona.id, { color: c })}
                              className="w-4 h-4 rounded-full border-2"
                              style={{ backgroundColor: c, borderColor: persona.color === c ? '#1F2937' : 'transparent' }} />
                          ))}
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {sessions.length === 0 && <p className="text-[9px] text-gray-300 px-1 py-1">No participants in this snapshot.</p>}
                          {sessions.map((s, i) => {
                            const inThis = (persona.sessionIds || []).includes(s.id);
                            const other = roleBySession[s.id] && roleBySession[s.id].id !== persona.id ? roleBySession[s.id] : null;
                            return (
                              <button key={s.id}
                                onClick={() => (inThis ? unassignParticipant(persona.id, s.id) : assignParticipant(persona.id, s.id))}
                                className="w-full flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 text-left">
                                <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border shrink-0 ${inThis ? 'border-transparent' : 'border-gray-300'}`}
                                  style={{ backgroundColor: inThis ? persona.color : 'transparent' }}>
                                  {inThis && (
                                    <svg viewBox="0 0 8 8" width="8" height="8">
                                      <path d="M1 4L3 6L7 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-[10px] font-bold text-gray-600">{pLabel(i)}</span>
                                {other && <span className="text-[8px] font-bold ml-auto" style={{ color: other.color }}>{other.label}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {editable && (
                  <div className="sticky left-0 z-10 p-2 bg-gray-50/40" style={{ width: 240 }}>
                    <button onClick={addPersona}
                      className="text-[9px] font-black text-rutabaga-pink hover:text-rutabaga-pink-hover uppercase tracking-widest">
                      + Add role
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyMapAnalysis;
